import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Box,
  Slider,
  Collapse,
} from "@material-ui/core";

import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CachedIcon from "@material-ui/icons/Cached";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import api from "../services/api";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import useSocketListener from "../hooks/useSocketListener";
import { FaGlobe } from "react-icons/fa";
import { logInfo, logError } from "../utils/logger";

const backendUrl = getBackendUrl();
const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "8px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },

  toolbar: {
    paddingRight: 24,
    color: theme.palette.dark.main,
    background: theme.palette.primary.main,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    minHeight: "48px",
    [theme.breakpoints.down("sm")]: {
      height: "48px",
    },
    backgroundColor: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  menuButtonHidden: {
    display: "none",
  },

  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
    fontWeight: 600,
    letterSpacing: "0.025em",
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
    borderRight: `1px solid ${theme.mode === "light" ? "#e0e0e0" : "#424242"}`,
    boxShadow:
      theme.mode === "light"
        ? "2px 0 8px rgba(0, 0, 0, 0.1)"
        : "2px 0 8px rgba(0, 0, 0, 0.3)",
  },

  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },

  appBarSpacer: {
    minHeight: "48px",
  },

  content: {
    flex: 1,
    overflow: "auto",
    padding: 0,
    margin: 0,
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: "8px",
    border: "2px solid transparent",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  logo: {
    width: "100%",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },

  hideLogo: {
    display: "none",
  },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      borderColor: theme.palette.primary.main,
    },
  },

  animatedBadge: {
    "& .MuiBadge-badge": {
      animation: "$heartbeat 2s infinite",
    },
  },

  "@keyframes heartbeat": {
    "0%": { transform: "scale(1)" },
    "14%": { transform: "scale(1.1)" },
    "28%": { transform: "scale(1)" },
    "42%": { transform: "scale(1.1)" },
    "70%": { transform: "scale(1)" },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },
  },
}))(Badge);

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  // ✅ Volume interno 0–1 (compatível), UI em 0–100%
  const [volume, setVolume] = useState(() => {
    const raw = localStorage.getItem("volume");
    const parsed = raw !== null ? Number(raw) : 1;

    // compatibilidade: se alguém salvou 0–100, converte para 0–1
    if (Number.isFinite(parsed) && parsed > 1) return Math.max(0, Math.min(1, parsed / 100));
    return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : 1;
  });

  // ✅ controla lista expansível de idiomas dentro do menu
  const [languagesOpen, setLanguagesOpen] = useState(false);

  // ✅ gera os itens do menu lateral
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const { getAll } = useSettings();

  // ✅ (FIX PRINCIPAL DO "PISCANDO"): não use document.body.offsetWidth em deps
  // Usa listener de resize + aplica uma vez na montagem
  useEffect(() => {
    const applyResponsiveLayout = () => {
      const width = window.innerWidth;

      setDrawerVariant(width < 600 ? "temporary" : "permanent");

      if (width > 600) {
        setDrawerOpen(user.defaultMenu !== "closed");
      }
    };

    applyResponsiveLayout();
    window.addEventListener("resize", applyResponsiveLayout);

    return () => window.removeEventListener("resize", applyResponsiveLayout);
  }, [user.defaultMenu]);

  // ✅ efeito separado para tema (evita loop de layout)
  useEffect(() => {
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultTheme, theme.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ foto do perfil
  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId) return;

    const savedProfileImage = localStorage.getItem("profileImage");
    const currentProfileImage = savedProfileImage || user.profileImage;

    if (currentProfileImage) {
      setProfileUrl(`${backendUrl}/public/company${companyId}/user/${currentProfileImage}`);
    } else {
      setProfileUrl(`${backendUrl}/public/app/noimage.png`);
    }
  }, [user?.companyId, user?.profileImage]);

  // ✅ anúncios
  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/for-company", {
        params: { status: true, pageNumber: "1" },
      });

      const activeAnnouncements = data.records.filter((announcement) => {
        const isActive = announcement.status === true || announcement.status === "true";
        const isNotExpired =
          !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
        return isActive && isNotExpired;
      });

      setAnnouncements(activeAnnouncements);
      setShowAnnouncementsModal(activeAnnouncements.length > 0);
    } catch (err) {
      toastError(err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) fetchAnnouncements();
  }, [user?.id, fetchAnnouncements]);

  useEffect(() => {
    if (!selectedAnnouncement) {
      setAckChecked(false);
      return;
    }
    setAckChecked(false);
  }, [selectedAnnouncement, user?.companyId]);

  const handleToggleAcknowledge = async (announcementId, checked) => {
    try {
      if (checked) {
        await api.post(`/announcements/${announcementId}/ack`);
        setAnnouncements((prev) => prev.filter((a) => a.id !== announcementId));
        setSelectedAnnouncement(null);
      } else {
        await api.delete(`/announcements/${announcementId}/ack`);
        await fetchAnnouncements();
      }

      setShowAnnouncementsModal(() => announcements.length - 1 > 0);
    } catch (err) {
      toastError(err);
    }
  };

  // ✅ socket listeners
  const handleAuthEvent = useCallback(
    (data) => {
      if (data.user.id === +user?.id) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    },
    [user?.id]
  );

  const handleUserUpdate = useCallback(
    (data) => {
      if (data.action === "update" && data.user.id === +user?.id) {
        if (data.user.profileImage) {
          const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
          setProfileUrl(newProfileUrl);
          localStorage.setItem("profileImage", data.user.profileImage);
        }
      }
    },
    [user?.companyId, user?.id]
  );

  const handleUserBirthday = useCallback(
    (data) => {
      logInfo("Evento de aniversário de usuário recebido", { data });
      if (data.userId === +user?.id) setShowBirthdayModal(true);
    },
    [user?.id]
  );

  const handleContactBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de contato recebido", { data });
  }, []);

  const checkBirthdaysOnLogin = useCallback(async () => {
    if (!user?.id || !user?.companyId) return;

    try {
      const { data } = await api.get("/birthdays/today");
      const birthdayData = data.data;

      const userBirthday = birthdayData.users.find((u) => u.id === +user.id);
      if (userBirthday) setShowBirthdayModal(true);

      if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
        logInfo("Há aniversariantes hoje", { birthdayData });
      }
    } catch (error) {
      logError("Erro ao verificar aniversários", error);
    }
  }, [user?.id, user?.companyId]);

  useSocketListener(socket, user, "auth", handleAuthEvent);
  useSocketListener(socket, user, "user", handleUserUpdate);
  useSocketListener(socket, user, "user-birthday", handleUserBirthday);
  useSocketListener(socket, user, "contact-birthday", handleContactBirthday);

  useEffect(() => {
    if (user?.id && user?.companyId) {
      const timer = setTimeout(() => checkBirthdaysOnLogin(), 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");
      const interval = setInterval(() => socket.emit("userStatus"), 1000 * 60 * 5);
      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => setUpdateInProgress(true);
  const handleUpdateComplete = () => setUpdateInProgress(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
    setLanguagesOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleRefreshPage = () => window.location.reload(false);

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find((s) => s.key === "enabledLanguages")?.value;

        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) langs = JSON.parse(enabledLanguagesSetting);
        } catch {}

        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId, getAll]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  // ✅ Volume: muda durante o arraste, mas só grava no localStorage no commit (evita travar no mobile)
  const handleVolumePercentChange = (_, newPercent) => {
    const normalized = Math.max(0, Math.min(1, Number(newPercent) / 100));
    setVolume(normalized);
  };

  const handleVolumePercentCommit = (_, newPercent) => {
    const normalized = Math.max(0, Math.min(1, Number(newPercent) / 100));
    localStorage.setItem("volume", String(normalized));
  };

  if (loading || updateInProgress) return <BackdropLoading />;

  return (
    <div className={clsx(classes.root, "logged-in-layout")}>
      {!hideMenu && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
          }}
          open={drawerOpen}
        >
          <div className={classes.toolbarIcon}>
            <img
              className={drawerOpen ? classes.logo : classes.hideLogo}
              style={{ display: "block", margin: "0 auto", width: "100%" }}
              alt="logo"
            />
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
              <ChevronLeftIcon />
            </IconButton>
          </div>

          <List className={classes.containerWithScroll}>{mainListItems}</List>
          <Divider />
        </Drawer>
      )}

      <AppBar
        position="absolute"
        className={clsx(classes.appBar, !hideMenu && drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {!hideMenu && (
            <IconButton
              edge="start"
              variant="contained"
              aria-label="open drawer"
              style={{ color: "white" }}
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={clsx(drawerOpen && classes.menuButtonHidden)}
            >
              <img
                src={`${backendUrl}/public/ico.png`}
                alt="Expandir menu"
                width={30}
                height={30}
                style={{ display: "block" }}
              />
            </IconButton>
          )}

          <Typography component="h2" variant="h6" color="inherit" noWrap className={classes.title}>
            <b>{user?.company?.name}</b>
          </Typography>

          {!hideMenu && (
            <>
              <VersionControl onUpdateStart={handleUpdateStart} onUpdateComplete={handleUpdateComplete} />

              {user?.id && <NotificationsPopOver volume={volume} />}

              <AnnouncementsPopover />
              <ChatPopover />

              <div className="user-menu-wrapper">
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  onClick={handleMenu}
                >
                  <Avatar alt={user?.name} className={classes.avatar2} src={profileUrl} />
                </StyledBadge>

                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                  userId={user?.id}
                />

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  disableScrollLock

                  // ✅ (FIX "piscando"): reduz briga de foco/hover em alguns browsers
                  disableAutoFocusItem
                  MenuListProps={{ autoFocusItem: false }}
                  ModalProps={{
                    disableRestoreFocus: true,
                    disableEnforceFocus: true,
                  }}

                  PaperProps={{
                    style: {
                      width: "min(92vw, 320px)",
                      maxWidth: "92vw",
                      overflowX: "hidden",
                    },
                  }}
                >
                  {/* CARD DO USUÁRIO */}
                  <MenuItem
                    onClick={handleOpenUserModal}
                    style={{ paddingTop: 18, paddingBottom: 18, paddingLeft: 16, paddingRight: 16 }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 6,
                      }}
                    >
                      <Avatar
                        alt={user?.name}
                        src={profileUrl}
                        className={classes.avatar2}
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #e6e6e6",
                        }}
                      />

                      <div
                        title="Clique para ver detalhes do usuário"
                        style={{
                          marginTop: 6,
                          fontWeight: 700,
                          fontSize: 18,
                          lineHeight: "22px",
                          color: "#111",
                          cursor: "pointer",
                        }}
                      >
                        {user?.name}
                      </div>

                      {!!user?.company?.name && (
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#9a9a9a", lineHeight: "18px" }}>
                          {user.company.name}
                        </div>
                      )}

                      {greaterThenSm && user?.profile === "admin" && user?.company?.dueDate && (
                        <div style={{ fontSize: 13, color: "#b0b0b0", lineHeight: "16px" }}>
                          {i18n.t("mainDrawer.appBar.user.active")} {dateToClient(user.company.dueDate)}
                        </div>
                      )}
                    </div>
                  </MenuItem>

                  <Divider />

                  {/* Alternar tema */}
                  <MenuItem onClick={colorMode.toggleColorMode}>
                    <IconButton>
                      {theme.mode === "dark" ? (
                        <Brightness7Icon style={{ color: "white" }} />
                      ) : (
                        <Brightness4Icon style={{ color: "#474747" }} />
                      )}
                    </IconButton>
                    Alternar modo
                  </MenuItem>

                  {/* Atualizar página */}
                  <MenuItem onClick={handleRefreshPage}>
                    <IconButton aria-label={i18n.t("mainDrawer.appBar.refresh")}>
                      {theme.mode === "dark" ? (
                        <CachedIcon style={{ color: "white" }} />
                      ) : (
                        <CachedIcon style={{ color: "#474747" }} />
                      )}
                    </IconButton>
                    Atualizar página
                  </MenuItem>

                  {/* ✅ VOLUME (sempre aberto) - responsivo e sem travar no mobile */}
                  <MenuItem
                    disableRipple
                    disableTouchRipple
                    style={{ cursor: "default", padding: "10px 12px", maxWidth: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      width="100%"
                      maxWidth="100%"
                      style={{ overflow: "hidden" }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchMove={(e) => e.stopPropagation()}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" style={{ fontWeight: 700, color: "#666" }}>
                          Volume
                        </Typography>
                        <Typography variant="caption" style={{ color: "#888", marginLeft: 8 }}>
                          {Math.round((Number.isFinite(volume) ? volume : 1) * 100)}%
                        </Typography>
                      </Box>

                      <Slider
                        value={Math.round((Number.isFinite(volume) ? volume : 1) * 100)}
                        onChange={handleVolumePercentChange}
                        onChangeCommitted={handleVolumePercentCommit}
                        min={0}
                        max={100}
                        step={1}
                        aria-label="volume"
                        style={{ width: "100%" }}
                      />
                    </Box>
                  </MenuItem>

                  {/* ✅ IDIOMA (lista dentro do menu, sem popup) */}
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setLanguagesOpen((prev) => !prev);
                    }}
                  >
                    <IconButton style={{ padding: 6 }}>
                      <FaGlobe />
                    </IconButton>
                    Idioma
                    <div style={{ flex: 1 }} />
                    {languagesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </MenuItem>

                  <Collapse in={languagesOpen} timeout="auto" unmountOnExit>
                    {filteredLanguageOptions.map((lang) => (
                      <MenuItem
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        style={{ paddingLeft: 52, fontSize: 14, color: "#444" }}
                      >
                        {lang.label}
                      </MenuItem>
                    ))}
                  </Collapse>

                  <Divider />

                  {/* Logout */}
                  <MenuItem onClick={handleClickLogout}>
                    {i18n.t("mainDrawer.appBar.user.logout")}
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}
        </Toolbar>
      </AppBar>

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>

              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {selectedAnnouncement.text}
              </Typography>

              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Checkbox
                    color="primary"
                    checked={ackChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAckChecked(checked);
                      handleToggleAcknowledge(selectedAnnouncement.id, checked);
                    }}
                  />
                }
                label="Estou ciente e não mostrar novamente"
              />

              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              )}

              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="textPrimary">
                          Prioridade:{" "}
                          {announcement.priority === 1 ? "Alta" : announcement.priority === 2 ? "Média" : "Baixa"}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowAnnouncementsModal(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />
    </div>
  );
};

export default LoggedInLayout;
